import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/Error';
import UIMap from '@/components/shared/UIMap';

interface ConstraintError {
  property?: string;
  constraints?: Record<string, string>;
}

interface RecordEntry {
  valid: boolean;
  row: number;
  rawData: Record<string, any>;
  errors: (ConstraintError | string)[];
}

interface UploadResult {
  originalName: string;
  allValid: boolean;
  totalRecords: number;
  validCount: number;
  invalidCount: number;
  records: RecordEntry[];
  dataFileName: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  result: UploadResult;
}

function parseErrors(errors: (ConstraintError | string)[]) {
  const fieldErrors: Record<string, string[]> = {};
  const generalErrors: string[] = [];

  errors.forEach((err) => {
    if (typeof err === 'string') {
      generalErrors.push(err);
    } else if (err.property && err.constraints) {
      fieldErrors[err.property] = Object.values(err.constraints);
    }
  });

  return { fieldErrors, generalErrors };
}

const ImportResultRow: React.FC<{
  record: RecordEntry;
  headers: string[];
}> = ({ record, headers }) => {
  const { fieldErrors, generalErrors } = parseErrors(record.errors);

  const isInvalidRow = generalErrors.length > 0;

  return (
    <>
      <TableRow
        sx={{
          '& td, & th': {
            px: 1,
            py: 0.5,
            fontSize: '0.75rem',
          },
          bgcolor: isInvalidRow ? '#f8d7da' : 'transparent',
        }}
      >
        <TableCell>
          <IconButton
            sx={{
              color: record.valid ? 'green' : '#e74c3c',
              fontSize: '1rem',
            }}
            size="small"
          >
            {record.valid ? (
              <CheckIcon fontSize="inherit" />
            ) : (
              <ErrorIcon fontSize="inherit" />
            )}
          </IconButton>
        </TableCell>
        <TableCell>{record.row}</TableCell>
        {headers.map((field) => {
          if (field === 'Map') {
            return (
              <TableCell key="map">
                {record.rawData.latitude && record.rawData.longitude && (
                  <div style={{ width: '150px', height: '100px' }}>
                    <UIMap
                      latitude={parseFloat(record.rawData.latitude)}
                      longitude={parseFloat(record.rawData.longitude)}
                    />
                  </div>
                )}
              </TableCell>
            );
          }

          const value = record.rawData[field];
          const errors = fieldErrors[field] || [];
          const hasError = errors.length > 0;

          const content =
            value === null || value === undefined ? (
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontSize: '0.75rem' }}
              >
                Empty value
              </Typography>
            ) : hasError ? (
              <Tooltip title={errors.join(', ')} arrow>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: '0.75rem' }}
                >
                  {value.toString()}
                </Typography>
              </Tooltip>
            ) : (
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontSize: '0.75rem' }}
              >
                {value.toString()}
              </Typography>
            );

          return (
            <TableCell
              key={field}
              sx={{
                bgcolor: hasError ? '#f8d7da' : 'transparent',
                color: hasError ? 'error.contrastText' : 'inherit',
                whiteSpace: 'nowrap',
                fontSize: '0.75rem',
                px: 1,
                py: 0.5,
              }}
            >
              {content}
            </TableCell>
          );
        })}
      </TableRow>

      {generalErrors.length > 0 && (
        <TableRow>
          <TableCell colSpan={headers.length + 2}>
            <Typography
              variant="body2"
              color="error"
              sx={{ fontSize: '0.75rem' }}
            >
              {generalErrors.join(', ')}
            </Typography>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const BatchImportResultModal: React.FC<Props> = ({
  open,
  onClose,
  onConfirm,
  result,
}) => {
  const headers = useMemo(() => {
    const originalHeaders = Object.keys(result.records[0]?.rawData || {});
    const longitudeIndex = originalHeaders.indexOf('longitude');
    if (longitudeIndex !== -1) {
      return [
        ...originalHeaders.slice(0, longitudeIndex + 1),
        'Map',
        ...originalHeaders.slice(longitudeIndex + 1),
      ];
    }
    return originalHeaders;
  }, [result]);

  const isImportEnabled = result.records.some((record) => record.valid);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          width: '95%',
          height: '90vh',
          maxWidth: 'none',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle sx={{ fontSize: '1rem', py: 1 }}>
        Import result: {result.originalName}
      </DialogTitle>

      <DialogContent sx={{ flex: 1, overflow: 'hidden' }}>
        <Typography
          variant="subtitle2"
          gutterBottom
          sx={{ fontSize: '0.8rem' }}
        >
          Total records: {result.totalRecords}, valid: {result.validCount},
          with errors: {result.invalidCount}
        </Typography>

        <TableContainer
          component={Paper}
          sx={{ maxHeight: '100%', overflow: 'auto' }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow
                sx={{
                  '& th': {
                    px: 1,
                    py: 0.5,
                    fontSize: '0.75rem',
                    whiteSpace: 'nowrap',
                  },
                }}
              >
                <TableCell>Status</TableCell>
                <TableCell>No.</TableCell>
                {headers.map((key) => (
                  <TableCell key={key}>{key}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {result.records.map((record) => (
                <ImportResultRow
                  key={record.row}
                  record={record}
                  headers={headers}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1 }}>
        <Button onClick={onClose} size="small">
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          disabled={!isImportEnabled}
          variant="contained"
          size="small"
        >
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BatchImportResultModal;
